import { ChangeDetectionStrategy, Component, ViewChild, computed, inject, input, signal } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { TypeManagerDecorator } from "../../../main/type.map.service";
import { Bind } from "../../../decorators/bind.decorator";
import { s25Resource } from "../../../util/s25.resource";
import { SeOrgSecurityStore } from "../se.org.security.store";
import {
    Connect25EmailTemplateApi,
    isOrgOwnedEmailTemplate,
    type SeEmailTemplateDto,
} from "./connect25.email.template.api";
import {
    buildEmailTemplatePayload,
    refreshEmailTemplateTable,
    sortEmailTemplatesByName,
} from "./connect25.email.template.form.util";
import { S25RichTextEditorModule } from "../../s25-rich-text-editor/s25.rich.text.editor.module";
import { S25TableModule } from "../../s25-table/s25.table.module";
import { Table } from "../../s25-table/Table";
import { GenericTableButtonComponent } from "../../s25-table/generics/generic.table.button.component";
import { S25TableComponent } from "../../s25-table/s25.table.component";
import { AlertService } from "../../../services/alert.service";
import { S25Util } from "../../../util/s25-util";
import { S25FadingSuccessMsgComponent } from "../../s25-fading-success-msg/s25.fading.success.msg.component";

@TypeManagerDecorator("s25-ng-connect25-email-template-org")
@Component({
    selector: "s25-ng-connect25-email-template-org",
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonModule, FormsModule, S25RichTextEditorModule, S25TableModule, S25FadingSuccessMsgComponent],
    template: `
        @if (orgSecStore.canViewOrgEmail()) {
            <div class="connect25-email-template-org">
                @if (!formVisible()) {
                    <h2>Email templates</h2>
                    <p class="connect25-email-template-org__intro c-margin-bottom--double">
                        Templates shared by your institution are read-only. Your organization can create and manage its
                        own templates here.
                    </p>

                    @if (templatesResource.error()) {
                        <p class="c-text-danger" role="alert">Could not load templates.</p>
                    } @else if (templatesResource.isLoading() && !templatesResource.value()) {
                        <p>Loading…</p>
                    } @else {
                        @if (orgSecStore.canManageOrgEmailTemplates()) {
                            <div class="connect25-email-template-org__toolbar c-margin-bottom--single">
                                <button type="button" class="aw-button aw-button--outline" (click)="startCreate()">
                                    Create template
                                </button>
                            </div>
                        }
                        <div class="connect25-email-template-org__table-wrap">
                            <s25-ng-table
                                #templatesTable
                                [caption]="'Email templates'"
                                [dataSource]="templatesTableSource()"
                                [hasRefresh]="true"
                                [hasFilter]="true"
                                [columnSortable]="true"
                                [showHeaderWhenNoData]="true"
                                [noResultsMessage]="'No email templates yet.'"
                                [unlimitedWidth]="true"
                            />
                        </div>
                    }
                } @else {
                    <div class="c-margin-bottom--single">
                        <button type="button" class="aw-button aw-button--outline" (click)="backToList()">
                            ← Back to template list
                        </button>
                    </div>
                    <h2>{{ formTitle() }}</h2>
                    @if (formLoadError()) {
                        <p class="c-text-danger" role="alert">{{ formLoadError() }}</p>
                    }
                    @if (formLoading()) {
                        <p>Loading template…</p>
                    } @else {
                        <div class="connect25-email-template-org__grid">
                            <label class="connect25-email-template-org__field">
                                <span class="connect25-email-template-org__label"
                                    >Template name
                                    @if (!formReadOnly()) {
                                        <span class="c-text-danger">*</span>
                                    }
                                </span>
                                <input
                                    type="text"
                                    class="c-input"
                                    [ngModel]="formTemplateName()"
                                    (ngModelChange)="formTemplateName.set($event)"
                                    [readonly]="formReadOnly()"
                                    required
                                />
                            </label>
                            <label class="connect25-email-template-org__field">
                                <span class="connect25-email-template-org__label">Subject</span>
                                <input
                                    type="text"
                                    class="c-input"
                                    [ngModel]="formSubject()"
                                    (ngModelChange)="formSubject.set($event)"
                                    [readonly]="formReadOnly()"
                                />
                            </label>
                            @if (!formReadOnly()) {
                                <label
                                    class="connect25-email-template-org__field connect25-email-template-org__field--checkbox"
                                >
                                    <input
                                        type="checkbox"
                                        [ngModel]="formEnabled()"
                                        (ngModelChange)="formEnabled.set($event)"
                                    />
                                    <span>Enabled</span>
                                </label>
                            } @else {
                                <p class="connect25-email-template-org__readonly-enabled">
                                    <strong>Enabled:</strong> {{ formEnabled() ? "Yes" : "No" }}
                                </p>
                            }
                            <div class="connect25-email-template-org__field connect25-email-template-org__field--body">
                                <span class="connect25-email-template-org__label">Body</span>
                                @if (formReadOnly()) {
                                    <div
                                        class="connect25-email-template-org__body-preview"
                                        [innerHTML]="formBodyHtml()"
                                    ></div>
                                } @else {
                                    <s25-ng-rich-text-editor
                                        class="connect25-email-template-org__rte"
                                        [modelValue]="formBodyHtml()"
                                        (modelValueChange)="formBodyHtml.set($event)"
                                        [defaultHeight]="220"
                                    />
                                }
                            </div>
                        </div>
                        @if (!formReadOnly()) {
                            <div class="c-margin-top--double connect25-email-template-org__actions">
                                <div class="connect25-email-template-org__action-buttons">
                                    <button
                                        type="button"
                                        class="aw-button aw-button--primary"
                                        (click)="save()"
                                        [disabled]="saving()"
                                    >
                                        {{ editingId() ? "Save changes" : "Create template" }}
                                    </button>
                                    <button
                                        type="button"
                                        class="aw-button aw-button--danger--outline c-margin-left--single"
                                        (click)="backToList()"
                                    >
                                        Cancel
                                    </button>
                                </div>
                                <s25-ng-fading-success-msg
                                    class="connect25-email-template-org__save-success"
                                    [(message)]="saveSuccessMessage"
                                />
                            </div>
                        }
                    }
                }
            </div>
        } @else {
            <p>You do not have access to organization email templates.</p>
        }
    `,
    styles: `
        .connect25-email-template-org h2 {
            margin-bottom: 0.75rem;
            font-size: 1.25rem;
            font-weight: 600;
        }
        .connect25-email-template-org__intro {
            max-width: 52rem;
        }
        .connect25-email-template-org__toolbar {
            display: flex;
            flex-wrap: wrap;
            gap: 0.75rem;
            align-items: center;
        }
        .connect25-email-template-org__grid {
            display: grid;
            gap: 1rem;
            max-width: 52rem;
        }
        .connect25-email-template-org__field {
            display: flex;
            flex-direction: column;
            gap: 0.35rem;
            align-items: flex-start;
        }
        .connect25-email-template-org__field--body {
            align-self: stretch;
            width: 100%;
        }
        .connect25-email-template-org__field--checkbox {
            flex-direction: row;
            align-items: center;
            gap: 0.5rem;
        }
        .connect25-email-template-org__label {
            font-weight: 600;
        }
        .connect25-email-template-org__rte {
            display: block;
            width: 100%;
        }
        :host ::ng-deep .connect25-email-template-org__rte .tox-tinymce {
            width: 100%;
        }
        .connect25-email-template-org__body-preview {
            width: 100%;
            padding: 0.75rem;
            border: 1px solid var(--color--border-bold);
            border-radius: 4px;
            background: var(--color--surface-bg);
        }
        .connect25-email-template-org__actions {
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            gap: 0.75rem;
        }
        .connect25-email-template-org__action-buttons {
            display: flex;
            flex-wrap: wrap;
            align-items: center;
        }
        .connect25-email-template-org__save-success {
            min-height: 1.5rem;
        }
    `,
})
export class Connect25EmailTemplateOrgComponent {
    @ViewChild("templatesTable") private templatesTable?: S25TableComponent;

    readonly orgId = input.required<number>();

    protected readonly orgSecStore = inject(SeOrgSecurityStore);

    readonly templatesResource = s25Resource({
        params: () => this.orgId(),
        if: () => this.orgSecStore.canViewOrgEmail(),
        loader: ({ params: orgId }) => Connect25EmailTemplateApi.listForOrg(orgId, false, false),
    });

    readonly sortedTemplates = computed(() => sortEmailTemplatesByName(this.templatesResource.value() ?? []));

    readonly templatesTableSource = computed((): Table.DataSource => {
        const columns: Table.Column[] = [
            { id: "templateName", header: "Name", minWidth: 160 },
            { id: "subject", header: "Subject", minWidth: 160 },
            { id: "source", header: "Source", minWidth: 110 },
            { id: "enabled", header: "Enabled", minWidth: 80 },
            {
                id: "edit",
                header: "Edit",
                width: "min-content",
                align: "center",
                sortable: false,
                export: false,
            },
        ];
        if (this.orgSecStore.canManageOrgEmailTemplates()) {
            columns.push({
                id: "delete",
                header: "Delete",
                width: "min-content",
                align: "center",
                sortable: false,
                export: false,
            });
        }
        return {
            type: "unpaginated",
            dataSource: this.getTemplateTableRows,
            columns,
        };
    });

    readonly formVisible = signal(false);
    readonly formLoading = signal(false);
    readonly formLoadError = signal<string | undefined>(undefined);
    readonly saving = signal(false);
    readonly editingId = signal<number | undefined>(undefined);
    readonly formReadOnly = signal(false);

    readonly formTemplateName = signal("");
    readonly formSubject = signal("");
    readonly formBodyHtml = signal("");
    readonly formEnabled = signal(true);
    readonly saveSuccessMessage = signal("");

    readonly formTitle = computed(() => {
        if (this.formReadOnly()) {
            return "View template";
        }
        return this.editingId() ? "Edit template" : "New template";
    });

    @Bind
    async getTemplateTableRows(query: Table.UnpaginatedQuery): Promise<Table.DataSourceResponse> {
        if (query.forceRefresh) {
            await this.templatesResource.reloadAndAwait();
        }
        const orgId = this.orgId();
        const canManage = this.orgSecStore.canManageOrgEmailTemplates();
        const templates = this.sortedTemplates();
        return {
            rows: templates.map((template) => {
                const orgOwned = isOrgOwnedEmailTemplate(template, orgId);
                const cells: Table.Row["cells"] = {
                    templateName: { text: template.templateName ?? "" },
                    subject: { text: template.subject ?? "" },
                    source: {
                        text: orgOwned ? "Organization" : "Institution",
                    },
                    enabled: { text: template.enabled !== false ? "Yes" : "No" },
                    edit: this.buildEditOrViewCell(template, orgId),
                };
                if (canManage) {
                    cells.delete = orgOwned
                        ? this.buildDeleteCell(template)
                        : this.buildDisabledInstitutionDeleteCell();
                }
                return {
                    id: template.emailTemplateId!,
                    name: template.templateName,
                    data: { template },
                    cells,
                };
            }),
        };
    }

    private openActionLabel(template: SeEmailTemplateDto, orgId: number): string {
        if (this.orgSecStore.canManageOrgEmailTemplates() && isOrgOwnedEmailTemplate(template, orgId)) {
            return "Edit";
        }
        return "View";
    }

    private buildEditOrViewCell(template: SeEmailTemplateDto, orgId: number): Table.Cell {
        const label = this.openActionLabel(template, orgId);
        const templateId = template.emailTemplateId;
        return {
            component: GenericTableButtonComponent,
            inputs: {
                label,
                type: "outline",
                onClick: async () => {
                    if (templateId != null) {
                        await this.openTemplate(templateId, template);
                    }
                },
            },
            textValue: label,
        };
    }

    private buildDeleteCell(template: SeEmailTemplateDto): Table.Cell {
        return {
            component: GenericTableButtonComponent,
            inputs: {
                label: "Delete",
                type: "danger--outline",
                onClick: async () => {
                    await this.confirmDelete(template);
                },
            },
            textValue: "Delete",
        };
    }

    private buildDisabledInstitutionDeleteCell(): Table.Cell {
        const disabledReason = "Institution templates are read-only on this page.";
        return {
            component: GenericTableButtonComponent,
            inputs: {
                label: "Delete",
                // outline (not danger--outline): global danger hover styles ignore [disabled] and look clickable
                type: "outline",
                disabled: true,
                disabledReason,
            },
            textValue: "Delete",
        };
    }

    startCreate(): void {
        this.clearFormFields();
        this.formReadOnly.set(false);
        this.formVisible.set(true);
    }

    backToList(): void {
        this.clearFormFields();
        this.formVisible.set(false);
    }

    private clearFormFields(): void {
        this.editingId.set(undefined);
        this.formTemplateName.set("");
        this.formSubject.set("");
        this.formBodyHtml.set("");
        this.formEnabled.set(true);
        this.formReadOnly.set(false);
        this.formLoadError.set(undefined);
        this.formLoading.set(false);
        this.saveSuccessMessage.set("");
    }

    private async openTemplate(templateId: number, listRow?: SeEmailTemplateDto): Promise<void> {
        const orgId = this.orgId();
        const readOnly =
            !this.orgSecStore.canManageOrgEmailTemplates() ||
            (listRow != null && !isOrgOwnedEmailTemplate(listRow, orgId));

        this.formVisible.set(true);
        this.formLoading.set(true);
        this.formLoadError.set(undefined);
        this.editingId.set(templateId);
        this.formReadOnly.set(readOnly);

        try {
            const fullTemplate = await Connect25EmailTemplateApi.getForOrg(templateId, orgId);
            this.formTemplateName.set(fullTemplate.templateName ?? "");
            this.formSubject.set(fullTemplate.subject ?? "");
            this.formBodyHtml.set(fullTemplate.bodyHtml ?? "");
            this.formEnabled.set(fullTemplate.enabled !== false);
            const owned = isOrgOwnedEmailTemplate(fullTemplate, orgId);
            this.formReadOnly.set(readOnly || !owned);
        } catch (caughtError: unknown) {
            const message =
                caughtError instanceof Error && caughtError.message ? caughtError.message : "Could not load template.";
            this.formLoadError.set(message);
        } finally {
            this.formLoading.set(false);
        }
    }

    private buildPayload(): SeEmailTemplateDto {
        return buildEmailTemplatePayload({
            templateName: this.formTemplateName(),
            subject: this.formSubject(),
            bodyHtml: this.formBodyHtml(),
            enabled: this.formEnabled(),
            editingId: this.editingId(),
        });
    }

    private async refreshTemplatesTable(): Promise<void> {
        await refreshEmailTemplateTable({
            table: this.templatesTable,
            reloadResource: () => this.templatesResource.reloadAndAwait(),
        });
    }

    async save(): Promise<void> {
        if (this.saving() || this.formReadOnly()) {
            return;
        }
        const templateName = this.formTemplateName().trim();
        if (!templateName) {
            await AlertService.alert("Template name is required.");
            return;
        }
        const orgId = this.orgId();
        this.saving.set(true);
        try {
            const payload = this.buildPayload();
            const editingTargetId = this.editingId();
            const saved =
                editingTargetId != null
                    ? await Connect25EmailTemplateApi.updateOrgTemplate(orgId, payload)
                    : await Connect25EmailTemplateApi.createOrgTemplate(orgId, payload);
            await this.refreshTemplatesTable();
            const savedId = saved.emailTemplateId;
            if (savedId != null) {
                this.editingId.set(savedId);
            }
            this.formTemplateName.set(saved.templateName ?? "");
            this.formSubject.set(saved.subject ?? "");
            this.formBodyHtml.set(saved.bodyHtml ?? "");
            this.formEnabled.set(saved.enabled !== false);
            this.saveSuccessMessage.set(editingTargetId != null ? "Changes saved." : "Template created.");
        } catch (caughtError: unknown) {
            S25Util.showError(caughtError);
        } finally {
            this.saving.set(false);
        }
    }

    async confirmDelete(template: SeEmailTemplateDto): Promise<void> {
        const orgId = this.orgId();
        if (!this.orgSecStore.canManageOrgEmailTemplates() || !isOrgOwnedEmailTemplate(template, orgId)) {
            return;
        }
        const templateId = template.emailTemplateId;
        if (templateId == null || !Number.isFinite(templateId)) {
            return;
        }
        const confirmed = await AlertService.confirm(
            `Delete template "${template.templateName ?? templateId}"? This cannot be undone.`,
        );
        if (!confirmed) {
            return;
        }
        try {
            await Connect25EmailTemplateApi.deleteOrgTemplate(templateId, orgId);
            if (this.editingId() === templateId) {
                this.backToList();
            }
            await this.refreshTemplatesTable();
        } catch (caughtError: unknown) {
            S25Util.showError(caughtError);
        }
    }
}
